<template>
    <v-tooltip location="top" color="secondary">
        <template v-slot:activator="{ props }">
            <div v-bind="props">
                <span
                    class="
                        text-caption text-medium-emphasis
                        d-inline-block
                        text-truncate
                        mt-0
                        mb-0
                        pl-3
                    "
                    style="max-width: 100%"
                    >{{ inputDetails.description }}
                </span>
                <v-select
                    color="secondary"
                    v-if="isEnum(inputDetails)"
                    v-model="input"
                    variant="filled"
                    :items="inputDetails.values"
                    required
                    :rules="[emptyCheck]"
                    :menu-props="{
                        closeOnContentClick: true,
                        closeOnClick: true,
                    }"
                >
                    <template v-slot:label>
                        <katex-element
                            v-if="inputDetails.latex"
                            :expression="
                                getLatexName(
                                    inputDetails.latex,
                                    inputDetails.isOptional
                                )
                            "
                        ></katex-element>
                        <span
                            v-else
                            v-html="
                                getGreekName(
                                    inputDetails.name,
                                    inputDetails.isOptional
                                )
                            "
                        />
                    </template>
                </v-select>
                <v-file-input
                    v-else-if="isFile(inputDetails)"
                    :accept="fileTypes(inputDetails)"
                    class="acInput"
                    variant="filled"
                    :prepend-icon="null"
                    append-inner-icon="mdi-paperclip"
                    persistent-hint
                    :rules="[emptyCheck]"
                    v-model="input"
                >
                    <template v-slot:label>
                        <katex-element
                            v-if="inputDetails.latex"
                            :expression="
                                getLatexName(
                                    inputDetails.latex,
                                    inputDetails.isOptional
                                )
                            "
                        ></katex-element>
                        <span
                            v-else
                            v-html="
                                getGreekName(
                                    inputDetails.name,
                                    inputDetails.isOptional
                                )
                            "
                        />
                    </template>
                </v-file-input>
                <v-text-field
                    v-else
                    label="Label"
                    color="secondary"
                    v-model="input"
                    required
                    class="acInput"
                    :rules="[
                        emptyCheck,
                        (v) =>
                            validInput(inputDetails) ||
                            inputDetails.name + ' is incorrectly formatted',
                    ]"
                >
                    <template v-slot:label>
                        <katex-element
                            v-if="inputDetails.latex"
                            :expression="
                                getLatexName(
                                    inputDetails.latex,
                                    inputDetails.isOptional
                                )
                            "
                        ></katex-element>
                        <span
                            v-else
                            v-html="
                                getGreekName(
                                    inputDetails.name,
                                    inputDetails.isOptional
                                )
                            "
                        />
                    </template>
                    <template v-slot:append-inner>
                        <v-tooltip
                            v-if="isComplexInput(inputDetails)"
                            location="top"
                            color="secondary"
                        >
                            <template v-slot:activator="{ props }">
                                <router-link
                                    to="/docs/explanation/JSON.html"
                                    target="_blank"
                                    style="text-decoration: none"
                                    ><v-icon v-bind="props"
                                        >mdi-code-json</v-icon
                                    ></router-link
                                >
                            </template>
                            JSON input
                        </v-tooltip>
                        <katex-element
                            v-else-if="inputDetails.units"
                            :expression="escapeUnits(inputDetails.units)"
                        ></katex-element>
                        <span
                            v-else
                            v-html="getUnits(inputDetails.description)"
                        />
                    </template>
                </v-text-field>
            </div>
        </template>
        <span v-html="inputTooltipText"></span>
    </v-tooltip>
</template>
<script setup>
import { ref, computed, watch } from 'vue'
import helpers from '../../../../helpers'

const props = defineProps(['inputDetails', 'modelValue'])
const emit = defineEmits(['update:modelValue'])

const { isEnum, valueToSend, getGreekName: helperGetGreekName } = helpers

const boolOptions = ['True', 'False']
const lazyInput = ref(props.modelValue)

watch(
    () => props.modelValue,
    (val) => {
        lazyInput.value = val
    }
)

const input = computed({
    get() {
        if (lazyInput.value === '' && isFile(props.inputDetails))
            return undefined
        return lazyInput.value
    },
    set(val) {
        lazyInput.value = val
        emit('update:modelValue', val)
    },
})

const inputTooltipText = computed(() => {
    let html = ''
    if (props.inputDetails.isOptional && props.inputDetails.defaultValue) {
        html += `<i>(Optional) Default = ${props.inputDetails.defaultValue}</i><br>`
    }
    if (props.inputDetails.description) html += props.inputDetails.description
    return html
})

function getLatexName(latex, isOptional) {
    return `${isOptional ? '(Optional) ' : ''}${latex}`
}

function emptyCheck(v) {
    if (!props.inputDetails.isOptional && v !== 0)
        return !!v || props.inputDetails.name + ' is required'
    return true
}

function validInput(input) {
    let valToSend = valueToSend(input)

    if (valToSend == 0) {
        // evaluates to false in js land
        return true
    } else if (valToSend) {
        return true
    } else if (input.isOptional && valToSend === null) {
        return true
    }

    return false
}

function getGreekName(name, isOptional) {
    return `${isOptional ? '<i>(Optional)</i> ' : ''}${helperGetGreekName(
        name
    )}`
}

function isFile(input) {
    return input.isFile
}

function fileTypes(input) {
    if (input.fileTypes) return input.fileTypes.join(',')
    return ''
}

function fileHint(input) {
    return `Supported format${
        input.fileTypes.length > 1 ? 's' : ''
    }: ${input.fileTypes.join(', ')}`
}

function isComplexInput(input) {
    if (
        input.type != 'System.Double' &&
        input.type != 'System.Int32' &&
        input.type != 'System.Boolean' &&
        input.type != 'System.String' &&
        input.type != 'string' &&
        input.type != 'str' &&
        input.type != 'float' &&
        input.type != 'int' &&
        input.type != 'bool' &&
        input.type != '' &&
        !isEnum(input)
    ) {
        return true
    }
    return false
}

function escapeUnits(units) {
    if (units == '%') {
        // % is a comment in latex, but a common unit
        return '\\%'
    }
    return units
}

function getUnits(description) {
    var regExp = /\{([^}]+)\}/
    var matches = regExp.exec(description)
    if (matches && matches.length > 1) {
        return matches[1].replace('^2', '<sup>2</sup>')
    }
    return ''
}
</script>
<style scoped>
:deep(.v-label) {
    height: auto;
}
:deep(.v-field--error .v-field__append-inner) {
    color: rgb(var(--v-theme-error));
}
</style>
